import type { DisplayDateFormat } from '@livechat/shared';
import { useEffect, useMemo, useRef, useState } from 'react';

import { formatDisplayDate, useDateFormat } from '../date-format.js';

export interface DateRangeValue {
	from: string;
	fromTime: string;
	to: string;
	toTime: string;
}

export type DateRangePreset = 'month' | 'today' | 'week';

function parseIsoDate(value: string): Date {
	const [year, month, day] = value.split('-').map(Number);
	return new Date(year!, month! - 1, day!);
}

export function isoDate(date: Date): string {
	return [
		String(date.getFullYear()).padStart(4, '0'),
		String(date.getMonth() + 1).padStart(2, '0'),
		String(date.getDate()).padStart(2, '0'),
	].join('-');
}

function addDays(date: Date, days: number): Date {
	const result = new Date(date);
	result.setDate(result.getDate() + days);
	return result;
}

function addMonths(date: Date, months: number): Date {
	return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function startOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function presetDateRange(
	preset: DateRangePreset = 'today',
	today = new Date(),
): DateRangeValue {
	const from =
		preset === 'today'
			? today
			: preset === 'week'
				? addDays(today, -((today.getDay() + 6) % 7))
				: startOfMonth(today);
	return {
		from: isoDate(from),
		fromTime: '00:00',
		to: isoDate(today),
		toTime: '23:59',
	};
}

export function recentDateRange(days = 7, today = new Date()): DateRangeValue {
	const inclusiveDays = Math.max(1, Math.floor(days));
	return {
		from: isoDate(addDays(today, 1 - inclusiveDays)),
		fromTime: '00:00',
		to: isoDate(today),
		toTime: '23:59',
	};
}

export function dateRangeLabel(
	value: DateRangeValue,
	includeTime = true,
	dateFormat: DisplayDateFormat = 'dd/mm/yyyy',
): string {
	return includeTime
		? `${formatDisplayDate(parseIsoDate(value.from), dateFormat)} ${value.fromTime} – ${formatDisplayDate(parseIsoDate(value.to), dateFormat)} ${value.toTime}`
		: `${formatDisplayDate(parseIsoDate(value.from), dateFormat)} – ${formatDisplayDate(parseIsoDate(value.to), dateFormat)}`;
}

export function orderedDateRange(
	first: string,
	second: string,
	fromTime = '00:00',
	toTime = '23:59',
): DateRangeValue {
	return first <= second
		? { from: first, fromTime, to: second, toTime }
		: { from: second, fromTime, to: first, toTime };
}

function validDateTimeRange(value: DateRangeValue, today: string): boolean {
	return Boolean(
		value.from &&
		value.fromTime &&
		value.to &&
		value.toTime &&
		value.from <= value.to &&
		value.to <= today &&
		(value.from !== value.to || value.fromTime <= value.toTime),
	);
}

function calendarDates(month: Date): (string | undefined)[] {
	const firstWeekday = (month.getDay() + 6) % 7;
	const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
	return [
		...Array<string | undefined>(firstWeekday).fill(undefined),
		...Array.from({ length: days }, (_, index) =>
			isoDate(new Date(month.getFullYear(), month.getMonth(), index + 1)),
		),
	];
}

function visibleMonthFor(value: DateRangeValue, today: string): Date {
	const selected = startOfMonth(parseIsoDate(value.from));
	const current = startOfMonth(parseIsoDate(today));
	return selected >= current ? addMonths(current, -1) : selected;
}

function CalendarMonth({
	dateFormat,
	draft,
	month,
	onPointerDown,
	onSelect,
	today,
}: {
	dateFormat: DisplayDateFormat;
	draft: DateRangeValue;
	month: Date;
	onPointerDown: (value: string) => void;
	onSelect: (value: string) => void;
	today: string;
}) {
	const monthLabel = new Intl.DateTimeFormat('en-GB', {
		month: 'long',
		year: 'numeric',
	}).format(month);
	return (
		<section className="range-calendar" aria-label={monthLabel}>
			<h4>{monthLabel}</h4>
			<div className="range-calendar-weekdays" aria-hidden="true">
				{['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
					<span key={day}>{day}</span>
				))}
			</div>
			<div className="range-calendar-days">
				{calendarDates(month).map((value, index) => {
					if (!value)
						return <span className="range-calendar-blank" key={`blank-${index}`} />;
					const isStart = value === draft.from;
					const isEnd = value === draft.to;
					const inRange = value > draft.from && value < draft.to;
					return (
						<button
							aria-label={formatDisplayDate(parseIsoDate(value), dateFormat)}
							aria-pressed={isStart || isEnd}
							className={[
								inRange ? 'in-range' : '',
								isStart ? 'range-start' : '',
								isEnd ? 'range-end' : '',
								value === today ? 'today' : '',
							]
								.filter(Boolean)
								.join(' ')}
							disabled={value > today}
							data-range-date={value}
							key={value}
							onClick={() => onSelect(value)}
							onPointerDown={() => onPointerDown(value)}
							type="button"
						>
							{Number(value.slice(-2))}
						</button>
					);
				})}
			</div>
		</section>
	);
}

export function DateRangePicker({
	includeTime = true,
	onChange,
	value,
}: {
	includeTime?: boolean;
	onChange: (range: DateRangeValue) => void;
	value: DateRangeValue;
}) {
	const { dateFormat } = useDateFormat();
	const today = useMemo(() => isoDate(new Date()), []);
	const wrapper = useRef<HTMLDivElement>(null);
	const dragSelection = useRef<{ last: string; moved: boolean; start: string } | undefined>(
		undefined,
	);
	const suppressClick = useRef(false);
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState(value);
	const [selectingEnd, setSelectingEnd] = useState(false);
	const [visibleMonth, setVisibleMonth] = useState(() => visibleMonthFor(value, today));
	const currentMonth = useMemo(() => startOfMonth(parseIsoDate(today)), [today]);

	useEffect(() => {
		if (!open) return;
		const closeOnOutsideClick = (event: PointerEvent) => {
			if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
		};
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false);
		};
		const updateDrag = (event: PointerEvent) => {
			const drag = dragSelection.current;
			if (!drag) return;
			const target = document
				.elementFromPoint(event.clientX, event.clientY)
				?.closest<HTMLButtonElement>('[data-range-date]');
			const selected = target?.dataset.rangeDate;
			if (!selected || target.disabled || selected > today || selected === drag.last) return;
			drag.last = selected;
			drag.moved ||= selected !== drag.start;
			setDraft((current) =>
				orderedDateRange(drag.start, selected, current.fromTime, current.toTime),
			);
			setSelectingEnd(false);
		};
		const endDrag = () => {
			if (dragSelection.current?.moved) {
				suppressClick.current = true;
				window.setTimeout(() => {
					suppressClick.current = false;
				}, 0);
			}
			dragSelection.current = undefined;
		};
		document.addEventListener('pointerdown', closeOnOutsideClick);
		document.addEventListener('pointermove', updateDrag);
		document.addEventListener('pointerup', endDrag);
		document.addEventListener('keydown', closeOnEscape);
		return () => {
			dragSelection.current = undefined;
			document.removeEventListener('pointerdown', closeOnOutsideClick);
			document.removeEventListener('pointermove', updateDrag);
			document.removeEventListener('pointerup', endDrag);
			document.removeEventListener('keydown', closeOnEscape);
		};
	}, [open, today]);

	function openPicker(): void {
		dragSelection.current = undefined;
		suppressClick.current = false;
		setDraft(value);
		setSelectingEnd(false);
		setVisibleMonth(visibleMonthFor(value, today));
		setOpen((current) => !current);
	}

	function selectDate(selected: string): void {
		if (suppressClick.current) {
			suppressClick.current = false;
			return;
		}
		if (!selectingEnd || selected < draft.from) {
			setDraft((current) => ({ ...current, from: selected, to: selected }));
			setSelectingEnd(true);
			return;
		}
		setDraft((current) => ({ ...current, to: selected }));
		setSelectingEnd(false);
	}

	function beginDrag(selected: string): void {
		dragSelection.current = { last: selected, moved: false, start: selected };
	}

	function selectPreset(preset: DateRangePreset): void {
		const range = presetDateRange(preset, parseIsoDate(today));
		setDraft(range);
		setSelectingEnd(false);
		setVisibleMonth(visibleMonthFor(range, today));
	}

	return (
		<div className="date-range-picker" ref={wrapper}>
			<button
				aria-expanded={open}
				aria-haspopup="dialog"
				className="date-range-trigger"
				onClick={openPicker}
				type="button"
			>
				<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
					<rect height="16" rx="2" width="18" x="3" y="5" />
					<path d="M7 3v4M17 3v4M3 10h18" />
				</svg>
				<span>
					<small>{includeTime ? 'Date and time range' : 'Date range'}</small>
					<strong>{dateRangeLabel(value, includeTime, dateFormat)}</strong>
				</span>
				<svg
					aria-hidden="true"
					className="date-range-chevron"
					fill="none"
					viewBox="0 0 24 24"
				>
					<path d="m7 10 5 5 5-5" />
				</svg>
			</button>

			{open && (
				<div className="date-range-popover" role="dialog" aria-label="Choose date range">
					<div className="date-range-popover-head">
						<div>
							<strong>
								{includeTime ? 'Choose dates and times' : 'Choose dates'}
							</strong>
							<span>
								{selectingEnd
									? 'Now choose an end date'
									: 'Drag across dates, or click a start and end date'}
							</span>
						</div>
						<div className="date-range-month-controls">
							<button
								aria-label="Previous months"
								onClick={() => setVisibleMonth((month) => addMonths(month, -1))}
								type="button"
							>
								‹
							</button>
							<button
								aria-label="Next months"
								disabled={addMonths(visibleMonth, 1) >= currentMonth}
								onClick={() => setVisibleMonth((month) => addMonths(month, 1))}
								type="button"
							>
								›
							</button>
						</div>
					</div>

					<div className="date-range-presets" aria-label="Quick date ranges">
						{(
							[
								['today', 'Today'],
								['week', 'This Week'],
								['month', 'This Month'],
							] as const
						).map(([preset, label]) => (
							<button key={preset} onClick={() => selectPreset(preset)} type="button">
								{label}
							</button>
						))}
					</div>

					<div className="date-range-calendars">
						<CalendarMonth
							dateFormat={dateFormat}
							draft={draft}
							month={visibleMonth}
							onPointerDown={beginDrag}
							onSelect={selectDate}
							today={today}
						/>
						<CalendarMonth
							dateFormat={dateFormat}
							draft={draft}
							month={addMonths(visibleMonth, 1)}
							onPointerDown={beginDrag}
							onSelect={selectDate}
							today={today}
						/>
					</div>

					<div className={`date-range-inputs${includeTime ? '' : ' date-only'}`}>
						<label>
							<span>Start date</span>
							<input
								max={draft.to}
								onChange={(event) => {
									const selected = event.currentTarget.value;
									if (!selected) return;
									setDraft((current) => ({
										...current,
										from: selected,
									}));
									setSelectingEnd(true);
								}}
								type="date"
								value={draft.from}
							/>
						</label>
						{includeTime && (
							<label>
								<span>Start time</span>
								<input
									onChange={(event) => {
										const fromTime = event.currentTarget.value;
										setDraft((current) => ({
											...current,
											fromTime,
										}));
									}}
									type="time"
									value={draft.fromTime}
								/>
							</label>
						)}
						<label>
							<span>End date</span>
							<input
								max={today}
								min={draft.from}
								onChange={(event) => {
									const selected = event.currentTarget.value;
									if (!selected) return;
									setDraft((current) => ({
										...current,
										to: selected,
									}));
									setSelectingEnd(false);
								}}
								type="date"
								value={draft.to}
							/>
						</label>
						{includeTime && (
							<label>
								<span>End time</span>
								<input
									onChange={(event) => {
										const toTime = event.currentTarget.value;
										setDraft((current) => ({
											...current,
											toTime,
										}));
									}}
									type="time"
									value={draft.toTime}
								/>
							</label>
						)}
					</div>

					<footer className="date-range-actions">
						<button
							className="secondary-button"
							onClick={() => setOpen(false)}
							type="button"
						>
							Cancel
						</button>
						<button
							className="primary-button"
							disabled={!validDateTimeRange(draft, today)}
							onClick={() => {
								onChange(draft);
								setOpen(false);
							}}
							type="button"
						>
							Apply range
						</button>
					</footer>
				</div>
			)}
		</div>
	);
}
